import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShippingProgress from "@/components/ShippingProgress";
import PreorderShippingNotice from "@/components/PreorderShippingNotice";
import { ArrowLeft, Trash2, Plus, Minus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const Cart = () => {
    const {
        items,
        updateQuantity,
        removeItem,
        subtotal,
        clearCart,
    } = useCart();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const handleCheckout = async () => {
        if (items.length === 0) {
            toast.error("Cart is empty.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                items: items.map((i) => ({
                    variant_id: i.variant_id,
                    quantity: i.quantity,
                })),
                origin_url: window.location.origin,
                customer_email: email || null,
                customer_name: name || null,
            };
            const r = await api.post("/checkout/session", payload);
            if (r.data.url) {
                window.location.href = r.data.url;
            } else {
                throw new Error("No checkout URL");
            }
        } catch (err) {
            logError(err);
            toast.error(
                err.response?.data?.detail ||
                    "Could not start checkout. Please try again.",
            );
            setLoading(false);
        }
    };

    return (
        <div data-testid="cart-page" className="bg-[#0B0E14] text-white min-h-screen">
            <Header />
            <main className="pt-32 pb-20">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
                    <button
                        onClick={() => navigate("/")}
                        data-testid="back-to-shop-link"
                        className="font-chivo text-xs tracking-[0.24em] uppercase text-slate-400 hover:text-[#ffffff] transition-colors flex items-center gap-2 mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Continue Shopping
                    </button>

                    <div className="bs-eyebrow">Your Order</div>
                    <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-wide mt-4 mb-12">
                        Review &{" "}
                        <span className="text-[#ffffff]">Deploy.</span>
                    </h1>

                    {items.length === 0 ? (
                        <div
                            data-testid="cart-empty-state"
                            className="bs-card p-12 text-center"
                        >
                            <div className="font-anton text-3xl uppercase text-white tracking-wider mb-2">
                                Cart is Empty
                            </div>
                            <p className="font-body text-slate-400 mb-8">
                                Reinforcements await. Browse the lineup.
                            </p>
                            <Link
                                to="/"
                                data-testid="empty-cart-shop-link"
                                className="btn-primary inline-flex"
                            >
                                Browse Roasts
                            </Link>
                        </div>
                    ) : (
                        <div className="grid lg:grid-cols-3 gap-10">
                            {/* Items */}
                            <div className="lg:col-span-2 space-y-4">
                                {items.map((item) => (
                                    <div
                                        key={item.variant_id}
                                        data-testid={`cart-row-${item.variant_id}`}
                                        className="bs-card p-4 md:p-5 flex flex-col sm:flex-row gap-4"
                                    >
                                        <div className="w-full sm:w-32 h-32 flex-shrink-0 overflow-hidden border border-[#1E293B]">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-[#ffffff]">
                                                        {item.badge}
                                                    </div>
                                                    <div className="font-anton text-xl uppercase text-white tracking-wide leading-tight">
                                                        {item.name}
                                                    </div>
                                                    <div className="font-body text-xs text-slate-400 mt-1">
                                                        {item.grind} · {item.size}
                                                    </div>
                                                </div>
                                                <button
                                                    data-testid={`cart-remove-${item.variant_id}`}
                                                    onClick={() =>
                                                        removeItem(
                                                            item.variant_id,
                                                        )
                                                    }
                                                    className="text-slate-500 hover:text-[#004466] transition-colors p-1"
                                                    aria-label="Remove"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="mt-auto pt-4 flex items-center justify-between">
                                                <div className="flex items-center border border-[#1E293B]">
                                                    <button
                                                        data-testid={`cart-decrease-${item.variant_id}`}
                                                        onClick={() =>
                                                            updateQuantity(
                                                                item.variant_id,
                                                                item.quantity -
                                                                    1,
                                                            )
                                                        }
                                                        className="p-2 hover:bg-[#1E2530] text-white"
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                    <span
                                                        data-testid={`cart-qty-${item.variant_id}`}
                                                        className="px-4 font-chivo text-white"
                                                    >
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        data-testid={`cart-increase-${item.variant_id}`}
                                                        onClick={() =>
                                                            updateQuantity(
                                                                item.variant_id,
                                                                item.quantity +
                                                                    1,
                                                            )
                                                        }
                                                        className="p-2 hover:bg-[#1E2530] text-white"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div className="font-anton text-2xl text-[#ffffff]">
                                                    $
                                                    {(
                                                        item.price *
                                                        item.quantity
                                                    ).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    onClick={() => clearCart()}
                                    data-testid="clear-cart-button"
                                    className="font-chivo text-xs tracking-[0.24em] uppercase text-slate-500 hover:text-[#004466] transition-colors"
                                >
                                    Clear Cart
                                </button>
                            </div>

                            {/* Summary */}
                            <div className="lg:col-span-1">
                                <div className="bs-card p-6 sticky top-28">
                                    <div className="font-anton text-2xl uppercase tracking-wide text-white mb-6">
                                        Order Summary
                                    </div>

                                    <PreorderShippingNotice />

                                    <div className="space-y-3 mb-5">
                                        <div>
                                            <label className="bs-label">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) =>
                                                    setEmail(e.target.value)
                                                }
                                                data-testid="checkout-email-input"
                                                className="bs-input"
                                                placeholder="for receipt"
                                            />
                                        </div>
                                        <div>
                                            <label className="bs-label">
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) =>
                                                    setName(e.target.value)
                                                }
                                                data-testid="checkout-name-input"
                                                className="bs-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="bs-stencil-divider my-5" />

                                    <div className="flex items-center justify-between font-body text-slate-300 mb-2">
                                        <span>Subtotal</span>
                                        <span data-testid="cart-summary-subtotal">
                                            ${subtotal.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between font-body text-slate-500 text-sm mb-1">
                                        <span>Shipping</span>
                                        <span>Calc. at checkout</span>
                                    </div>
                                    <ShippingProgress subtotal={subtotal} />
                                    <div className="flex items-center justify-between font-body text-slate-500 text-sm mb-5">
                                        <span>Tax</span>
                                        <span>Calc. at checkout</span>
                                    </div>

                                    <div className="bs-stencil-divider my-5" />

                                    <div className="flex items-center justify-between mb-6">
                                        <span className="font-chivo text-xs tracking-[0.22em] uppercase text-slate-400">
                                            Total
                                        </span>
                                        <span className="font-anton text-3xl text-white">
                                            ${subtotal.toFixed(2)}
                                        </span>
                                    </div>

                                    <button
                                        onClick={handleCheckout}
                                        disabled={loading}
                                        data-testid="checkout-button"
                                        className="btn-primary w-full justify-center disabled:opacity-60"
                                    >
                                        {loading ? "Processing..." : "Secure Checkout"}
                                    </button>

                                    <div className="flex items-center gap-2 mt-4 text-xs font-body text-slate-500">
                                        <ShieldCheck className="w-4 h-4 text-[#ffffff]" />
                                        Secure payment via Stripe.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Cart;
